/**
 * Diagnostic Codes Dialog Component
 * Collects ICD-10 and CPT codes for insurance sessions before claim submission
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { servicePackageService } from '../../services/servicePackageService'
import api from '../../services/api'
import { createFilterOptions } from '@mui/material/Autocomplete'

interface DiagnosticCodesDialogProps {
  open: boolean
  onClose: () => void
  session: any
  contractId: string
  patient?: any
  onSuccess: () => void
}

interface CodeOption {
  id: string
  code: string
  description: string
}

const DiagnosticCodesDialog: React.FC<DiagnosticCodesDialogProps> = ({
  open,
  onClose,
  session,
  contractId,
  patient,
  onSuccess,
}) => {
  const [selectedIcdCodes, setSelectedIcdCodes] = useState<CodeOption[]>([])
  const [selectedCptCodes, setSelectedCptCodes] = useState<CodeOption[]>([])
  const [icdOptions, setIcdOptions] = useState<CodeOption[]>([])
  const [cptOptions, setCptOptions] = useState<CodeOption[]>([])
  const [icdInputValue, setIcdInputValue] = useState('')
  const [cptInputValue, setCptInputValue] = useState('')
  const [loadingIcd, setLoadingIcd] = useState(false)
  const [loadingCpt, setLoadingCpt] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cptFilterOptions = createFilterOptions<CodeOption>({
    matchFrom: 'any',
    stringify: (option) => option.description ? `${option.code} ${option.description}` : option.code,
  })

  // Load CPT codes on mount
  useEffect(() => {
    if (open) {
      loadCptCodes()
      loadIcdCodes('') // Load ICD codes even without search term
    }
  }, [open])

  // Load ICD codes when input changes (with debounce)
  useEffect(() => {
    if (open) {
      const timeoutId = setTimeout(() => {
        loadIcdCodes(icdInputValue)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [icdInputValue, open])

  const loadCptCodes = async () => {
    setLoadingCpt(true)
    try {
      const codes = await servicePackageService.fetchCptCodes()
      setCptOptions(codes)
    } catch (err) {
      console.error('Failed to load CPT codes', err)
      setError('Failed to load CPT codes. Please try again.')
    } finally {
      setLoadingCpt(false)
    }
  }

  const loadIcdCodes = async (search: string = '') => {
    setLoadingIcd(true)
    try {
      // Fetch codes with search term (empty string will fetch all available codes)
      const codes = await servicePackageService.fetchIcdCodes(search)
      setIcdOptions(codes)
    } catch (err) {
      console.error('Failed to load ICD-10 codes', err)
      setError('Failed to load ICD-10 codes. Please try again.')
    } finally {
      setLoadingIcd(false)
    }
  }

  const handleSubmit = async () => {
    if (selectedCptCodes.length === 0) {
      setError('Please select at least one CPT code.')
      return
    }

    if (selectedIcdCodes.length === 0) {
      setError('Please select at least one ICD-10 code.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const sessionId = (session as any)._id || session.id

      const payload = {
        contract_id: contractId,
        session_id: sessionId,
        icd_code: selectedIcdCodes.map(code => code.id),
        cpt_code: selectedCptCodes.map(code => code.id),
      }

      await api.post('/sessions/add-dignostic-code-session', payload)

      // Show success and proceed
      onSuccess()
      handleClose()
    } catch (err: any) {
      console.error('Error submitting diagnostic codes:', err)
      setError(
        err.response?.data?.message ||
          'Failed to submit diagnostic codes. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setSelectedIcdCodes([])
    setSelectedCptCodes([])
    setIcdInputValue('')
    setCptInputValue('')
    setError(null)
    onClose()
  }

  // Format session date
  const sessionDate = session?.scheduledAt
    ? format(new Date(session.scheduledAt), 'MMM dd, yyyy')
    : 'N/A'

  // Get session duration
  const duration = session?.slotDurationMinutes || session?.duration || 60

  // Get patient name
  const patientName = patient?.name
    ? patient.name
    : 'N/A'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <Box sx={{ p: 4 }}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            mb={3}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: '#1F2937',
                  fontSize: '1.75rem',
                  mb: 0.5,
                }}
              >
                Add Diagnostic Codes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select ICD-10 and CPT codes for this session
              </Typography>
            </Box>
            <Button
              onClick={handleClose}
              sx={{
                minWidth: 'auto',
                p: 1,
                color: 'grey.500',
                '&:hover': { color: 'grey.700' },
              }}
            >
              <CloseIcon />
            </Button>
          </Box>

          {/* Info Alert */}
          <Alert
            icon={<InfoIcon />}
            severity="info"
            sx={{
              mb: 3,
              backgroundColor: '#EFF6FF',
              color: '#1E40AF',
              '& .MuiAlert-icon': {
                color: '#3B82F6',
              },
            }}
          >
            These codes will be used for insurance claim submission. Please
            ensure accuracy.
          </Alert>

          {/* Session Summary */}
          <Card
            sx={{
              mb: 3,
              backgroundColor: '#F0F9FF',
              borderRadius: 2,
              border: '1px solid #E0F2FE',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: '#1F2937', mb: 2 }}
              >
                Session Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Date:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {sessionDate}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Duration:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {duration} mins
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Patient:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {patientName}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* CPT Codes Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                CPT Codes <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select Current Procedural Terminology codes. At least one CPT
                code is required.
              </Typography>

              <Autocomplete
                multiple
                options={cptOptions.filter(
                  option =>
                    !selectedCptCodes.some(
                      selected => selected.id === option.id
                    )
                )}
                getOptionLabel={option =>
                  option.description
                    ? `${option.code} - ${option.description}`
                    : option.code
                }
                filterOptions={cptFilterOptions}
                value={selectedCptCodes}
                inputValue={cptInputValue}
                onChange={(_, newValue) => {
                  setSelectedCptCodes(newValue)
                }}
                onInputChange={(_, newInputValue) => {
                  setCptInputValue(newInputValue)
                }}
                loading={loadingCpt}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select CPT Codes"
                    placeholder="Search CPT codes..."
                  />
                )}
                renderTags={() => null}
              />

              {selectedCptCodes.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected CPT Codes ({selectedCptCodes.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCptCodes.map(code => (
                      <Chip
                        key={code.id}
                        label={`${code.code} - ${code.description || ''}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedCptCodes(
                            selectedCptCodes.filter(c => c.id !== code.id)
                          )
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* ICD-10 Codes Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ICD-10 Codes <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select International Classification of Diseases codes. At least
                one ICD-10 code is required.
              </Typography>

              <Autocomplete
                multiple
                options={icdOptions.filter(
                  option =>
                    !selectedIcdCodes.some(selected => selected.id === option.id)
                )}
                getOptionLabel={option =>
                  option.description
                    ? `${option.code} - ${option.description}`
                    : option.code
                }
                filterOptions={(x) => x}
                value={selectedIcdCodes}
                inputValue={icdInputValue}
                onChange={(_, newValue) => {
                  setSelectedIcdCodes(newValue)
                }}
                onInputChange={(_, newInputValue) => {
                  setIcdInputValue(newInputValue)
                }}
                loading={loadingIcd}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.code}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Search ICD-10 Codes"
                    placeholder="Type to search ICD-10 codes..."
                    helperText="Start typing to search for ICD-10 codes"
                  />
                )}
                renderTags={() => null}
              />

              {selectedIcdCodes.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected ICD-10 Codes ({selectedIcdCodes.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedIcdCodes.map(code => (
                      <Chip
                        key={code.id}
                        label={`${code.code} - ${code.description || ''}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => {
                          setSelectedIcdCodes(
                            selectedIcdCodes.filter(c => c.id !== code.id)
                          )
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            borderColor: '#D1D5DB',
            color: '#6B7280',
            '&:hover': {
              borderColor: '#9CA3AF',
              backgroundColor: '#F9FAFB',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          disabled={
            submitting ||
            selectedCptCodes.length === 0 ||
            selectedIcdCodes.length === 0
          }
          startIcon={!submitting && <CheckIcon />}
          sx={{
            textTransform: 'none',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            backgroundColor: '#2E7DAF',
            '&:hover': {
              backgroundColor: '#2563a0',
            },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Submitting...
            </>
          ) : (
            'Continue to Claim'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DiagnosticCodesDialog
